import React from 'react';
import {t, plural} from '@lingui/core/macro';

import CitiesIcon from 'shared/ui/icons/NavMenu/CitiesIcon';
import {IHttpMonitorItem} from 'features/http/model/HttpMonitor/IHttpMonitor';

import {GeoStatusState} from '../geoStatusState';
import LocationsTable from '../LocationsTable/LocationsTable';
import ProgressCircle from '../ProgressCircle/ProgressCircle';

import styles from './GeoStatusCard.module.scss';

interface IGeoStatusCardProps {
    item: IHttpMonitorItem;
    state: GeoStatusState;
}

const getCardTitle = (state: GeoStatusState, fail: number): string => {
    switch (state) {
        case 'all_ok':
            return t`Сайт доступен из всех локаций`;
        case 'warning':
            return plural(fail, {
                one: '# локация фиксирует ошибки',
                few: '# локации фиксируют ошибки',
                many: '# локаций фиксируют ошибки',
                other: '# локаций фиксируют ошибки',
            });
        case 'incident':
            return t`Сайт недоступен`;
        case 'paused':
            return t`Мониторинг по локациям на паузе`;
        case 'waiting':
        default:
            return t`Ожидаем первую проверку`;
    }
};

const getCardDescription = (
    state: GeoStatusState,
    fail: number,
    total: number,
    threshold: number,
): string => {
    switch (state) {
        case 'all_ok':
            return t`Инцидент откроется, когда количество локаций с ошибками достигнет заданного \
порога: ${threshold} из ${total}. Так мониторинг не реагирует на единичные сетевые сбои.`;
        case 'warning':
            return t`Инцидент пока не открыт: недоступность подтвердили ${fail} из ${total} локаций. \
Порог срабатывания — ${threshold} из ${total}. Чаще всего так проявляется локальная проблема в \
отдельном регионе или точке проверки.`;
        case 'incident':
            return t`Недоступность подтвердили ${fail} из ${total} локаций. Порог срабатывания — \
${threshold} из ${total}. Текущий результат соответствует условиям открытия инцидента.`;
        case 'paused':
            return t`Возобновите мониторинг, чтобы снова проверять сайт из ${total} локаций. \
Так вы будете видеть актуальный статус.`;
        case 'waiting':
        default:
            return t`Скоро проверим сайт из ${total} локаций. Страница обновится автоматически, \
когда появятся первые результаты.`;
    }
};

const GeoStatusCard: React.FC<IGeoStatusCardProps> = ({item, state}) => {
    const total = item.total_locations_count ?? 0;
    const fail = item.fail_locations_count ?? 0;
    const success = item.success_locations_count ?? 0;
    const lastResults = item.last_results ?? [];
    const showTable = state !== 'paused' && state !== 'waiting' && lastResults.length > 0;

    return (
        <div className={styles.card}>
            <div className={styles.summary}>
                <ProgressCircle success={success} total={total} state={state}/>
                <div className={styles.summaryText}>
                    <span className={styles.label}>
                        <CitiesIcon/>
                        {t`Геомониторинг`}
                    </span>
                    <span className={styles.title}>
                        {getCardTitle(state, fail)}
                    </span>
                    <span className={styles.description}>
                        {getCardDescription(state, fail, total, item.threshold)}
                    </span>
                </div>
            </div>
            {showTable && (
                <>
                    <span className={styles.divider}/>
                    <LocationsTable results={lastResults}/>
                </>
            )}
        </div>
    );
};

export default GeoStatusCard;
